import 'react-app-polyfill/stable'
import '@/styles/index.scss'
import 'swiper/css/bundle'
import { datadogRum } from '@datadog/browser-rum'
import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { getSearchParams } from '@tmap-web-lib/utils'
import toLower  from 'lodash-es/toLower'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'
import { store } from './store'
import { setResponse } from './store/app'
import {
  setAccessKey,
  setCarrierName,
  setDeviceId,
  setDevToolsAccessKey,
  setEuk,
  setSessionId,
  setAccessKeyExpired,
} from './store/settings'
import { pickAccessKey } from './store/settings/storage-utils'
import { init as initFrontmanClient } from '@tmap-web-lib/remote-api-client/frontman'
import { UAParser } from 'ua-parser-js'
import smoothscroll from 'smoothscroll-polyfill'
import { initMockClient } from './api'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { StrictMode } from 'react'
import { TmapLog } from '@tmap-web-lib/tmap-log-client'
import { router } from '@/routes'
import { initMoengage, setMoengageUserId, setMoengageUserAttrs } from '@/utils/moengage'

smoothscroll.polyfill()

const ua = UAParser()
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // react query는 기본적으로 요청이 실패하면 3번의 재시도
      retry: 0,
      refetchOnWindowFocus: false,
      cacheTime: 5 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
      // 최초 설정에서 useErrorBoundary: true 면 4XX 에러를 에러 객체로 반환해주지 않음. (각 query hooks에서 false로 줘도 변경되지 않음)
      useErrorBoundary: true,
    },
  },
})

const rootNode = document.getElementById('root')!

const getRtgUrl = (href: string) => {
  if (href.startsWith('https://tscore.tmobiweb.com')) {
    return href.replace(/^(https:\/\/)(tscore.*?\.)?(.*)/, '$1tscore-rtg.$3')
  } else if (href.startsWith('https://tscore-stg.tmobiweb.com')) {
    return href.replace(/^(https:\/\/)(tscore.*?\.)?(.*)/, '$1tscore-dtg.$3')
  }
  return
}

const errorMessage = '예상치 못한 오류 가 발생 했습니다.\n고객센터에 문의해 주세요.'

TmapApp.getUserSetting({ key: 'local.server_type' })
  .then(serverType => {
    if (serverType === 'rtg') {
      const href = window.location.href
      const rtgUrl = getRtgUrl(href)

      if (rtgUrl) {
        window.location.replace(rtgUrl)
        return
      }
    }

    Promise.all([
      TmapApp.getAccessKey(),
      TmapApp.getDeviceId(),
      TmapApp.getCarrierName(),
      TmapApp.getEUK(),
      TmapApp.getSessionId(),
    ])
      .then(([
        accessKey,
        deviceId,
        carrierName,
        euk,
        sessionId,
      ]) => {
        const isPrd = import.meta.env.VITE_BUILD_ENV === 'PRD' && serverType !== 'rtg'
        // 쿼리스트링 ak, apiKey는 실질적으로 웹브라우저 테스트용.
        // 통신사 이름이 2바이트 문자열(일본어, 중국어)인 경우 브라우저에서 API 에러 발생하여 영문, 숫자를 제외한 나머지는 ETC로 변경(TMAPWEB-1583)
        const reg = /^[a-zA-Z\d]*$/
        const carrier = reg.test(carrierName ?? '') ? carrierName : 'ETC'
        const datadogRumSettings = {
          applicationId: 'e3eed284-543a-4d8e-99af-1638b66c350f',
          clientToken: 'pub3bb5bc9b1bbd73867bc770639c3291a9',
          site: 'datadoghq.com',
          service: 'tmap-life',
          env: toLower(import.meta.env.VITE_BUILD_ENV),
          version: `${import.meta.env.VITE_BUILD_ENV}#${import.meta.env.BUILD_NUMBER}`,
          euk: euk,
          deviceId: deviceId,
          trackInteractions: true,
          trackUserInteractions: true,
        }
        store.dispatch(setAccessKey(pickAccessKey(accessKey || getSearchParams().ak)))
        store.dispatch(setCarrierName(carrier || ''))
        store.dispatch(setDeviceId(deviceId))
        store.dispatch(setEuk(euk))
        store.dispatch(setSessionId(sessionId))

        initFrontmanClient({
          hostUrl: import.meta.env.VITE_FRONTMAN_URL,
          headers: {
            Carrier: carrier || '',
            'Device-Id': deviceId || '',
            'App-Code': 'TMAP',
            'Os-Type': TmapApp.env.isAndroid ? 'AND' : 'IOS',
            'App-Version': TmapApp.env.appVersion,
            'Os-Version': ua.os.version || '',
            'Model-No': ua.device.model || '',
            Using: 'WEB_LIFE',
          },
          timeout: 30000,
          onRequest(config) {
            config.headers.AccessKey = store.getState().settings.accessKey

            if (config.url?.includes('/proxy')) {
              config.headers.Euk = store.getState().settings.euk
            }

            return config
          },
          onResponse(response) {
            store.dispatch(setResponse(response.data))
            return response
          },
          onAccessKeyChanged(newAccessKey: string) {
            if (newAccessKey) {
              TmapApp.updateAccessKey({ key: newAccessKey })
              store.dispatch(setAccessKey(newAccessKey))
              if (store.getState().settings.devToolsAccessKey) {
                store.dispatch(setDevToolsAccessKey(newAccessKey))
              }
            }
          },
          onAccessKeyExpired(error) {
            store.dispatch(setAccessKeyExpired())
            TmapLog('event')
              .setActionId('tscore:error:frontman:onAccessKeyExpired')
              .setCustom('error', error)
              .send({ skip: { logbox: true } })
          },
          onError(error) {
            TmapLog('event')
              .setActionId('tscore:error:frontman:onError')
              .setCustom('error', error)
              .send({ skip: { logbox: true } })
          },
        })
        initMockClient({
          onRequest(config) {
            config.headers.AccessKey = store.getState().settings.accessKey
            return config
          },
          onResponse(response) {
            store.dispatch(setResponse(response.data))
            return response
          },
          onAccessKeyChanged(newAccessKey: string) {
            if (newAccessKey) {
              TmapApp.updateAccessKey({ key: newAccessKey })
              store.dispatch(setAccessKey(newAccessKey))
              if (store.getState().settings.devToolsAccessKey) {
                store.dispatch(setDevToolsAccessKey(newAccessKey))
              }
            }
          },
          onAccessKeyExpired() {
            store.dispatch(setAccessKeyExpired())
          },
        })
        TmapLog.init({
          serviceId: 'drivinglife',
          appVersion: TmapApp.env.appVersion,
          carrier: carrier,
          deviceId: deviceId,
          sessionId: sessionId,
          ip: '',
          logbox: {
            env: isPrd ? 'PRD' : 'STG',
            token: isPrd ? 'F5CD39ECE530' : '3AF19605D63E',
            logVersion: `1.0-${toLower(import.meta.env.VITE_BUILD_ENV)}`,
            appId: 'tmap',
          },
          mixpanel: {
            token: isPrd ? 'DIL0QSV0PQST' : 'XWZ2PVBR7YOF',
            identity: euk,
            debug: !isPrd,
            api_host: isPrd
              ? 'https://log-collector.tmobiapi.com'
              : 'https://log-collector-stg.tmobiapi.com',
            api_method: 'POST',
            api_payload_format: 'base64',
          },
          onSend: (tmapLogBuilder) => {
            tmapLogBuilder
              .setPageType('WEB')
              .setCustom('deploy_env', toLower(import.meta.env.VITE_BUILD_ENV))
              .setEuk(euk)
          },
        })

        initMoengage({
          appId: import.meta.env.VITE_MOENGAGE_APP_ID,
          cluster: 'dc_1',
        })
        setMoengageUserId(euk).then(() => {
          setMoengageUserAttrs({
            deviceId,
            carrier,
            sessionId,
            buildEnv: import.meta.env.VITE_BUILD_ENV,
          })
        })

        datadogRum.init({
          ...datadogRumSettings,
          sessionSampleRate: isPrd ? 5 : 10,
          sessionReplaySampleRate: 0,
        })

        ReactDOM.createRoot(rootNode).render(
          <StrictMode>
            <Provider store={store}>
              <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
                <ReactQueryDevtools position="bottom-right" />
              </QueryClientProvider>
            </Provider>
          </StrictMode>,
        )
      })
      .catch(() => {
        TmapApp.makeDialogPopup({ title: '', msg: errorMessage })
      })
  })
  .catch(() => {
    TmapApp.makeDialogPopup({ title: '', msg: errorMessage })
  })
